import { useEffect, useMemo } from 'react'

import { faClose } from '@fortawesome/pro-light-svg-icons/faClose'
import Sidebar from '@space-core/components/sidebar'
import { ConfigSteps } from '@space-events/toggleUnbanMemberCommunityModal'

import ErrorBoundary from '@space-shell/common/components/errorBoundary'
import useMembership from '@space-shell/common/hooks/useMembership'
import { useAuthStore, useUserInfoStore } from '@space-shell/core/store'
import { lazyRemote } from '@space-shell/lib/runtime'
import { useUnbanMemberCommunityModal } from '@space-shell/modules/communities/unbanMemberCommunityModal/context/useUnbanMemberCommunityModal'

import CommunityConfigMenu from './components/communityConfigMenu'
import useCommunityConfigurationSidebar from './hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'

import {
  ContainerConfig,
  ContainerHeaderBody,
  ContainerSidebarContent,
  CustomIcon,
  CustomSidebar
} from './styles'
import type { CommunityConfigurationSidebarProps } from './types'

import AccessAndPrivacy from '../accessAndPrivacy'
import { CommunityConfigurationUnbanMember } from '../communityConfigurationUnbanMember'
import { MembersAndPermissions } from '../membersAndPermissions'

const GeneralConfig = lazyRemote('communityManagement/GeneralConfig')

const CommunityConfigurationSidebar = ({
  isOpen,
  closeModal,
  selectedStep,
  setSelectedStep
}: CommunityConfigurationSidebarProps) => {
  const { membershipSlug } = useMembership()
  const { handleSetCommunity } = useUnbanMemberCommunityModal()
  const { community } = useCommunityConfigurationSidebar()

  const { id, token, authorities, userName, ucode } = useAuthStore()
  const { avatar, email } = useUserInfoStore()

  useEffect(() => {
    handleSetCommunity(community)
  }, [handleSetCommunity, community])

  const parentAppUser = useMemo(() => {
    return {
      id,
      token,
      authorities,
      name: userName,
      email: email ?? '',
      avatar,
      ucode
    }
  }, [authorities, avatar, email, id, token, ucode, userName])

  const renderComponent = useMemo(() => {
    const COMPONENTS = {
      [ConfigSteps.GENERAL_CONFIG]: (
        <ErrorBoundary>
          <GeneralConfig
            parentAppUser={parentAppUser}
            communityId={community.id}
            membershipSlug={membershipSlug}
          />
        </ErrorBoundary>
      ),
      [ConfigSteps.ACCESS_AND_PRIVACY]: <AccessAndPrivacy isOpen={isOpen} />,
      [ConfigSteps.MEMBERS_AND_PERMISSIONS]: <MembersAndPermissions />,
      [ConfigSteps.UNBAN_MEBER]: <CommunityConfigurationUnbanMember />
    }

    return COMPONENTS[selectedStep]
  }, [parentAppUser, community.id, membershipSlug, isOpen, selectedStep])

  return (
    <CustomSidebar isOpen={isOpen}>
      <ContainerConfig>
        <CommunityConfigMenu selectedItem={selectedStep} setSelectedItem={setSelectedStep} />
        <ContainerHeaderBody>
          <Sidebar.Header className='_d-flex _justify-content-end'>
            <CustomIcon
              className='_text-gray-900'
              data-test='close-icon-sidebar'
              onClick={closeModal}
              icon={faClose}
            />
          </Sidebar.Header>
          <Sidebar.Body className=''>
            <ContainerSidebarContent className='_overflow-y-auto'>
              {renderComponent}
            </ContainerSidebarContent>
          </Sidebar.Body>
        </ContainerHeaderBody>
      </ContainerConfig>
    </CustomSidebar>
  )
}

export default CommunityConfigurationSidebar
